import { Box, Card, Stack, Typography } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { PageHeader } from '@karehero/llama/pwa/atoms/PageHeader/PageHeader';

import StatusBar from '@/designs-dev/device/StatusBar';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
import { PAGE_HEADER_TOP } from '@/karehero-app/shell/layout';
import { ScreenFloor } from '@/james-playground/aa-family-claim/ClaimScreen';
import { SENT } from '@/james-playground/aa-family-claim/AaFamilyClaim.content';

/**
 * What the family is left holding once the claim has gone to the adviser: what
 * was sent and who does what next. No reply time, because nothing sets one yet.
 */
const SentScreen = ({
  t,
  answers,
  left,
  sources,
  onHome,
  onDownload,
}: {
  t: (text: string) => string;
  answers: number;
  left: number;
  /** What the claim was built from, said as a line. */
  sources?: string;
  onHome: () => void;
  onDownload: () => void;
}) => (
  <ScreenRoot>
    <StatusBar />
    <Box
      component='main'
      tabIndex={0}
      aria-label={SENT.title}
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        px: 3,
        pb: 3,
        '&:focus-visible': { outline: 'none' },
      }}
    >
      <Stack sx={{ pt: PAGE_HEADER_TOP + 3 }} spacing={3}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: 'custom.limeGreen.200',
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '@keyframes sentIn': {
              from: { transform: 'scale(0.6)', opacity: 0 },
              to: { transform: 'scale(1)', opacity: 1 },
            },
            animation: 'sentIn .4s cubic-bezier(0.34, 1.56, 0.64, 1) both',
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        >
          <Icon icon={'circle-check' as never} fontSize={28} />
        </Box>
        <PageHeader
          title={SENT.title}
          description={t(SENT.description)}
          variant='h2'
        />

        <Stack spacing={1}>
          <Typography sx={{ color: 'text.secondary' }} variant='overline'>
            {SENT.summaryLabel}
          </Typography>
          <Card sx={{ p: 2 }}>
            <Stack spacing={1.25}>
              {[
                SENT.answers(answers),
                ...(sources ? [sources] : []),
                ...(left ? [SENT.left(left)] : []),
              ].map((line) => (
                <Stack
                  key={line}
                  sx={{ alignItems: 'center' }}
                  direction='row'
                  spacing={1.25}
                >
                  <Box sx={{ display: 'flex', color: 'primary.main' }}>
                    <Icon icon={'circle-check' as never} fontSize={14} />
                  </Box>
                  <Typography variant='body1'>{line}</Typography>
                </Stack>
              ))}
            </Stack>
          </Card>
        </Stack>

        <Stack spacing={1.5}>
          <Typography sx={{ color: 'text.secondary' }} variant='overline'>
            {SENT.nextLabel}
          </Typography>
          {SENT.next.map((line, i) => (
            <Stack key={line} direction='row' spacing={2}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  flexShrink: 0,
                  borderRadius: '50%',
                  border: 1,
                  borderColor: 'custom.neutral.200',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                aria-hidden
              >
                <Typography variant='caption'>{i + 1}</Typography>
              </Box>
              <Typography sx={{ pt: 0.25 }} variant='body1'>
                {t(line)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Box>
    <Box
      sx={{
        px: 3,
        pt: 1.5,
        pb: 0.5,
        borderTop: 1,
        borderColor: 'custom.neutral.200',
      }}
    >
      <Stack spacing={1.5}>
        <Button
          variant='primary'
          size='lg'
          isFullWidth
          ariaLabel={SENT.home}
          onPress={onHome}
        >
          {SENT.home}
        </Button>
        <Button
          variant='link'
          size='lg'
          isFullWidth
          ariaLabel={SENT.download}
          onPress={onDownload}
        >
          {SENT.download}
        </Button>
      </Stack>
    </Box>
    <ScreenFloor />
  </ScreenRoot>
);

export default SentScreen;
